<template>
  <b-overlay rounded="lg">
    <b-card title="Bulk Update Warranty" sub-title="Step 2 - Review & Confirm" class="mb-2 container-fluid">
      <b-col cols="12">
        <div class="d-flex justify-content-between mb-2">
          <div>
            <b-button size="md" @click="$router.push({ name: 'bulk-update-warranty' })" variant="primary" type="filled">Back to Step 1</b-button>
            <b-button size="md" variant="outline-secondary" class="ml-2" @click="$router.push({ name: 'bulk-update-warranty-history' })">View History</b-button>
          </div>
          <div v-if="!showUpdatedCertificates">
            <b-button size="md" variant="success" @click="confirmBulkUpdate" :disabled="!canConfirm || loading">
              <b-spinner small v-if="loading"></b-spinner>
              {{ loading ? 'Updating...' : 'Confirm Bulk Update Warranty' }}
            </b-button>
          </div>
          <div v-else>
            <b-button size="md" variant="success" @click="sendEmail" :disabled="sendingEmail">
              <b-spinner small v-if="sendingEmail"></b-spinner>
              {{ sendingEmail ? 'Sending...' : 'Send Email to Customers' }}
            </b-button>
          </div>
        </div>

        <!-- Original Table for Selection -->
        <div v-if="!showUpdatedCertificates">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">Select Certificates to Update</h5>
            <b-button variant="outline-primary" size="sm" @click="openWarrantyModal" :disabled="!hasSelectedItems">
              Edit Warranty Duration
            </b-button>
          </div>
          <b-table :items="rows" :fields="fields" small responsive>
            <template #head(selected)="{ }">
              <b-form-checkbox v-model="selectAll" @change="toggleAll"></b-form-checkbox>
            </template>
            <template #cell(selected)="{ item }">
              <b-form-checkbox v-model="item.selected"></b-form-checkbox>
            </template>
            <template #cell(installationDate)="{ item }">
              {{ formatDate(item.installationDate) }}
            </template>
            <template #cell(generalWarrantyEndDate)="{ item }">
              <span :class="{'text-muted': !item.selected}">{{ formatDate(item.generalWarrantyEndDate) }}</span>
            </template>
            <template #cell(currentDuration)="{ item }">
              <span :class="{'text-muted': !item.selected}">{{ item.generalWarrantyDuration }} months</span>
            </template>
            <template #cell(finalWarrantyDuration)="{ item }">
              <span :class="{'text-muted': !item.selected}">{{ item.finalWarrantyDuration }} months</span>
            </template>
          </b-table>
        </div>

        <!-- Updated Certificates Table -->
        <div v-if="showUpdatedCertificates">
          <h5 class="mb-3">Updated Certificates</h5>
          <b-table :items="lastUpdated" :fields="updatedFields" small responsive>
            <template #cell(installationDate)="{ item }">
              {{ formatDate(item.installationDate) }}
            </template>
            <template #cell(newEndDate)="{ item }">
              {{ formatDate(item.newEndDate) }}
            </template>
          </b-table>
        </div>
      </b-col>
    </b-card>

    <b-modal id="confirm-modal" ref="confirmModal" title="Confirm Bulk Update" @ok="doBulkUpdate">
      Are you sure you want to update warranty for selected certificates?
    </b-modal>

    <!-- Warranty Duration Edit Modal -->
    <b-modal id="warranty-modal" ref="warrantyModal" title="Edit Warranty Duration" @ok="updateWarrantyDuration" @cancel="cancelWarrantyEdit">
      <div class="form-group">
        <label>New Warranty Duration (months):</label>
        <b-form-input
          type="number"
          v-model.number="newWarrantyDuration"
          :min="minWarrantyDuration"
          :max="maxWarrantyDuration"
          ref="warrantyDurationInput"
          :class="{'is-invalid': newWarrantyDuration && (newWarrantyDuration < minWarrantyDuration || newWarrantyDuration > maxWarrantyDuration)}"
        ></b-form-input>
        <small class="form-text text-muted">
          Valid range: {{ minWarrantyDuration }} - {{ maxWarrantyDuration }} months
        </small>
        <div v-if="newWarrantyDuration && (newWarrantyDuration < minWarrantyDuration || newWarrantyDuration > maxWarrantyDuration)" class="invalid-feedback d-block">
          Warranty duration must be between {{ minWarrantyDuration }} and {{ maxWarrantyDuration }} months.
        </div>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import { mapActions } from 'vuex'
import { dateFormat } from '@/utils/utils'

export default {
  name: 'BulkUpdateWarrantyStep2',
  data() {
    return {
      fields: [
        { key: 'selected', label: ''},
        { key: 'barcode', label: 'Barcode'},
        { key: 'certNo', label: 'Cert No'},
        { key: 'itemModel', label: 'Item Model'},
        { key: 'installationDate', label: 'Installation Date'},
        { key: 'generalWarrantyEndDate', label: 'General Warranty End Date'},
        { key: 'pcode', label: 'PCODE'},
        { key: 'currentDuration', label: 'Current Duration (months)'},
        { key: 'finalWarrantyDuration', label: 'Changed Warranty Duration (months)'},
      ],
      updatedFields: [
        { key: "barcode", label: 'Barcode'},
        { key: 'certNo', label: 'Cert No'},
        { key: 'itemModel', label: 'Item Model'},
        { key: 'installationDate', label: 'Installation Date'},
        { key: 'newEndDate', label: 'New End Date'},
      ],
      rows: [],
      selectAll: false,
      lastUpdated: [],
      loading: false,
      sendingEmail: false,
      showUpdatedCertificates: false,
      maxWarrantyDuration: 40,
      minWarrantyDuration: 12,
      showWarrantyModal: false,
      editingItem: null,
      newWarrantyDuration: null,
    }
  },
  computed: {
    canConfirm() {
      const selected = this.rows.filter(r => r.selected)
      if (!selected.length) return false
      return selected.every(r => !!r.finalWarrantyDuration)
    },
    hasSelectedItems() {
      return this.rows.some(r => r.selected)
    },
  },
  created() {
    document.title = "Bulk Update Warranty - Step 2 | RSP";
    this.loadWarrantyOptions();
  },
  mounted() {
    const results = this.$store.getters["warranty/getBulkUpdateResults"];
    if (results && results.length > 0) {
      this.rows = results.map(r => ({
        ...r,
        selected: true,
        finalWarrantyDuration: r.generalWarrantyDuration || null,
      }))
      this.selectAll = this.rows.length > 0 && this.rows.every(r => r.selected)
    } else {
      // Fallback to localStorage if store is empty
      const saved = localStorage.getItem('bulkWarrantyStep1Results')
      if (saved) {
        const { results } = JSON.parse(saved)
        this.rows = (results || []).map(r => ({
          ...r,
          selected: true,
          finalWarrantyDuration: r.generalWarrantyDuration || null,
        }))
        this.selectAll = this.rows.length > 0 && this.rows.every(r => r.selected)
      }
    }
  },
  watch: {
    // Keep header checkbox in sync when individual rows change
    rows: {
      handler() {
        this.selectAll = this.rows.length > 0 && this.rows.every(r => r.selected)
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      confirmBulkUpdateWarranty: "warranty/confirmBulkUpdateWarranty",
      sendBulkUpdateEmail: "warranty/sendBulkUpdateEmail",
      getOptionWarranty: "warranty/getOptionWarranty"
    }),
    formatDate(d) {
      return d ? dateFormat(d) : ''
    },
    toggleAll() {
      this.rows.forEach(r => { r.selected = this.selectAll })
    },
    async loadWarrantyOptions() {
      try {
        const options = await this.getOptionWarranty()
        const generalDurationOption = options.find(opt => opt.recordStatus === 'generalDuration')
        if (generalDurationOption) {
          this.maxWarrantyDuration = generalDurationOption.month
        }
        
        // Get the processed options from store (which includes minValue processing)
        const optionWarranty = this.$store.getters["warranty/getOptionWarranty"]
        if (optionWarranty && optionWarranty.generalDuration) {
          // Find the maximum value from the generalDuration options
          const generalOptions = optionWarranty.generalDuration
          if (generalOptions.length > 0) {
            this.maxWarrantyDuration = Math.max(...generalOptions.map(opt => opt.value))
            this.minWarrantyDuration = Math.min(...generalOptions.map(opt => opt.value))
          }
        }
      } catch (error) {
        console.error('Failed to load warranty options:', error)
        this.$bvToast.toast('Failed to load warranty options', { title: 'Error', variant: 'danger', solid: true })
      }
    },
    openWarrantyModal() {
      this.newWarrantyDuration = null
      this.$refs.warrantyModal.show()
      this.$nextTick(() => {
        if (this.$refs.warrantyDurationInput && this.$refs.warrantyDurationInput.$el) {
          this.$refs.warrantyDurationInput.$el.focus()
        }
      })
    },
    updateWarrantyDuration() {
      if (!this.newWarrantyDuration || 
          this.newWarrantyDuration < this.minWarrantyDuration || 
          this.newWarrantyDuration > this.maxWarrantyDuration) {
        this.$bvToast.toast(`Warranty duration must be between ${this.minWarrantyDuration} and ${this.maxWarrantyDuration} months`, { 
          title: 'Invalid Duration', 
          variant: 'danger', 
          solid: true 
        })
        return false // Prevent modal from closing
      }
      
      // Update all selected items with the new warranty duration
      this.rows.forEach(row => {
        if (row.selected) {
          row.finalWarrantyDuration = this.newWarrantyDuration
        }
      })
      
      this.$bvToast.toast(`Updated warranty duration to ${this.newWarrantyDuration} months for selected items`, { 
        title: 'Success', 
        variant: 'success', 
        solid: true 
      })
      
      return true // Allow modal to close
    },
    cancelWarrantyEdit() {
      this.newWarrantyDuration = null
    },
    confirmBulkUpdate() {
      // validation selected rows have finalWarrantyDuration
      const invalid = this.rows.find(r => r.selected && !r.finalWarrantyDuration)
      if (invalid) {
        this.$bvToast.toast('Please fill Changed Warranty Duration for selected certificates.', { title: 'Validation', variant: 'danger', solid: true })
        // focus to the first invalid input using $refs (v-for refs are arrays)
        this.$nextTick(() => {
          const key = 'fwd-'+invalid.certNo
          const refEl = this.$refs[key]
          let el = null
          if (Array.isArray(refEl)) el = refEl[0]
          else el = refEl
          if (el && el.$el) {
            const input = el.$el.querySelector('input')
            if (input) {
              input.focus()
              input.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          } else if (el && el.focus) {
            el.focus()
          }
        })
        return
      }
      this.$refs.confirmModal.show()
    },
    async doBulkUpdate() {
      this.loading = true
      
      try {
        const selectedItems = this.rows.filter(r => r.selected && r.finalWarrantyDuration)
        
        const result = await this.confirmBulkUpdateWarranty(selectedItems)
        
        this.lastUpdated = result
        this.showUpdatedCertificates = true
        
        this.$bvToast.toast('Bulk update completed successfully', { title: 'Success', variant: 'success', solid: true })
      } catch (error) {
        console.error('Bulk update failed:', error)
        this.$bvToast.toast('Failed to update warranties', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.loading = false
      }
    },
    async sendEmail() {
      this.sendingEmail = true
      
      try {
        await this.sendBulkUpdateEmail(this.lastUpdated)
        this.$bvToast.toast('Email sent to customers successfully', { title: 'Success', variant: 'success', solid: true })
      } catch (error) {
        console.error('Failed to send email:', error)
        this.$bvToast.toast('Failed to send email to customers', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        this.sendingEmail = false
      }
    },
  },
}
</script>

<style scoped>
.container-fluid { padding: 12px; }

.is-invalid input {
  border-color: #e3342f !important;
  box-shadow: 0 0 0 0.2rem rgba(227,52,47,0.15) !important;
}
</style>